import React from "react";
import Button from "components/button/Button";
import BrandCard from "views/admin/brands/components/BrandCard";
import { debounce } from "lodash";
import { TiPlus } from "react-icons/ti";
import { toast } from "react-hot-toast";
import { Spinner } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { deleteBrand, getBrands } from "api/BrandAPI";
import { IBrand } from "../goods/types/brand.interface";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { PiDotsThreeBold } from "react-icons/pi";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { FiSearch } from "react-icons/fi";

const Brands = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [brands, setBrands] = React.useState<IBrand[]>([]);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    ob: NumberParam,
    search: StringParam,
    limit: NumberParam,
  });

  const [searchKey, setSearchKey] = React.useState<string>(null);
  const [nextPage, setNextPage] = React.useState<number>(null);
  const [prevPage, setPrevPage] = React.useState<number>(null);
  const [lastPage, setLastPage] = React.useState<number>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageState, setPageState] = React.useState({
    startIdx: 0,
    endIdx: 0,
  });
  const [pageNumbers, setPageNumbers] = React.useState<number[]>([]);

  const getBrandsWithParams = (page?: number, keyword?: string): void => {
    setIsLoading(true);
    const promise = getBrands(page, keyword);

    promise
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          setIsLoading(false);
          setBrands(res.data.data.data);
          setNextPage(res.data.data.meta.next_page);
          setPrevPage(res.data.data.meta.prev_page);
          setLastPage(res.data.data.meta.last_page);
          setCurrentPage(res.data.data.meta.current_page);
          setPageNumbers(
            Array.from(Array(res.data.data.meta.last_page + 1).keys()).slice(1)
          );
          if (res.data.data.meta.current_page === pageState.endIdx + 1) {
            setPageState({
              startIdx: pageState.endIdx,
              endIdx: pageState.endIdx + 5,
            });
          } else if (res.data.data.meta.current_page === pageState.startIdx) {
            setPageState({
              startIdx: pageState.startIdx - 5,
              endIdx: pageState.endIdx - 5,
            });
          }
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Gagal memuat brand!");
      });
  };

  const jumpForwardPage = () => {
    setPageState({
      startIdx: pageState.endIdx,
      endIdx: pageState.endIdx + 5,
    });
  };

  const jumpBackwardPage = () => {
    setPageState({
      startIdx: pageState.startIdx - 5,
      endIdx: pageState.endIdx - 5,
    });
  };

  const handleNextPage = () => {
    if (currentPage === lastPage) return;
    setCurrentPage(nextPage);
    getBrandsWithParams(nextPage, searchKey);
  };

  const handlePrevPage = () => {
    if (currentPage === 1) return;
    setCurrentPage(prevPage);
    getBrandsWithParams(prevPage, searchKey);
  };

  const handleSearchKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setSearchKey(e.target.value === "" ? null : e.target.value);
    getBrandsWithParams(currentPage, e.target.value);
    setQuery({
      page: currentPage,
      search: e.target.value === "" ? null : e.target.value,
    });
  };

  const debounceRes = React.useMemo(() => {
    return debounce(handleSearchKey, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = (slug: string): void => {
    const promise = deleteBrand(slug);

    toast.promise(promise, {
      loading: "Menghapus brand...",
      success: (res) => {
        if (res.status === 200) getBrandsWithParams(query.page, query.search);

        return res.data.message || "Berhasil menghapus brand!";
      },
      error: "Gagal menghapus brand!",
    });
  };

  React.useEffect(() => {
    getBrandsWithParams(query.page, query.search);
    setPageState({
      startIdx: 0,
      endIdx: 5,
    });

    return () => {
      debounceRes.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-fit w-full">
        {/* Recenlty Added setion */}
        <div className="mb-5 mt-5 flex items-center justify-between px-[26px]">
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
            Brand
          </h4>
          <div className="flex gap-3">
            <Button
              size="fit"
              variant="primary"
              children={
                <div className="flex items-center gap-1">
                  <TiPlus /> Brand
                </div>
              }
              onClick={() => {
                navigate("/admin/brands/add");
              }}
            />
          </div>
        </div>
        <div className="mb-5 flex w-full items-center justify-between gap-8 px-[26px]">
          <div className="col-span-8 flex h-12 w-full flex-3 items-center rounded-full border-2 border-gray-300 bg-gray-100 text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Coba cari nama brand..."
              onChange={(e) => debounceRes(e)}
              defaultValue={searchKey}
              className="block h-full w-full rounded-full bg-gray-100 text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
            />
          </div>
        </div>
        {/* NFTs trending card */}
        {!isLoading ? (
          brands?.length > 0 ? (
            <div className="z-20 grid grid-cols-1 gap-5 md:grid-cols-3">
              {brands.map((brand, idx) => (
                <div key={idx}>
                  <BrandCard
                    id={brand.id}
                    name={brand.name}
                    description={brand.description}
                    image={brand.image}
                    slug={brand.slug}
                    key={idx}
                    onDelete={() => handleDelete(brand.slug)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-80 items-center justify-center">
              <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                Belum ada brand yang ditambahkan
              </p>
            </div>
          )
        ) : (
          <div className="flex h-80 w-full flex-col items-center justify-center">
            <Spinner size="xl" className="text-brand-400" />
          </div>
        )}
        {!isLoading && brands?.length > 0 && (
          <div className="pagination my-5 flex w-full justify-center">
            <div className="pagination__item flex items-center gap-2">
              <button
                className="pagination__button"
                onClick={() => handlePrevPage()}
              >
                <BiChevronLeft className="text-xl" />
              </button>
              <div className="flex items-center justify-center gap-1">
                {pageState.startIdx >= 1 && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpBackwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
                {pageNumbers
                  .slice(pageState.startIdx, pageState.endIdx)
                  .map((page, idx) => (
                    <button
                      className={`pagination__button ${
                        currentPage === page ? "pagination__button--active" : ""
                      }`}
                      onClick={() => {
                        setCurrentPage(page);
                        getBrandsWithParams(page, searchKey);
                        setQuery({
                          page: page,
                          search: searchKey,
                        });
                      }}
                      key={idx}
                    >
                      {page}
                    </button>
                  ))}
                {pageState.endIdx <= lastPage && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpForwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
              </div>
              <button
                className="pagination__button"
                onClick={() => handleNextPage()}
              >
                <BiChevronRight className="text-xl" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Brands;
